import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { LogOut, User as UserIcon, BookOpen, Library, MessageSquare, BarChart3, Calendar, Battery, Target, GripVertical, Save, Image, Plus, Trash2, Type, Palette, Bell, Shield, Tag, PenSquare, Gift, FileText, HelpCircle, Video, X, DollarSign, Mail, Bookmark, CheckSquare, Timer, Settings, Heart, ClipboardCheck, Users, Phone, Sparkles, Handshake, Megaphone } from "lucide-react";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import LaunchTracker from "@/components/LaunchTracker";
import { AIAssistant } from "@/components/community/AIAssistant";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DashboardCard {
  id: string;
  to: string;
  icon: any;
  title: string;
  description: string;
  gradient: string;
  delay: string;
}

interface LayoutSection {
  id: string;
  type: 'cards' | 'tracker' | 'moodboard' | 'skool' | 'ai-assistant';
}

interface MoodBoardItem {
  id: number;
  type: "image" | "note";
  content: string;
  title?: string;
  color?: string;
}

interface BackgroundOption {
  id: string;
  name: string;
  value: string;
  type: 'gradient' | 'solid' | 'custom';
}

const presetBackgrounds: BackgroundOption[] = [
  { id: 'default', name: 'Default', value: 'mesh-gradient', type: 'gradient' },
  { id: 'ocean', name: 'Ocean', value: 'bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-blue-950 dark:via-cyan-950 dark:to-teal-950', type: 'gradient' },
  { id: 'sunset', name: 'Sunset', value: 'bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 dark:from-orange-950 dark:via-pink-950 dark:to-purple-950', type: 'gradient' },
  { id: 'forest', name: 'Forest', value: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-green-950 dark:via-emerald-950 dark:to-teal-950', type: 'gradient' },
  { id: 'lavender', name: 'Lavender', value: 'bg-gradient-to-br from-purple-50 via-violet-50 to-pink-50 dark:from-purple-950 dark:via-violet-950 dark:to-pink-950', type: 'gradient' },
  { id: 'minimal', name: 'Minimal', value: 'bg-background', type: 'solid' },
];

const defaultCards: DashboardCard[] = [
  {
    id: "profile",
    to: "/dashboard/profile",
    icon: UserIcon,
    title: "My Profile",
    description: "Manage your profile, avatar, and bio",
    gradient: "from-primary to-accent",
    delay: "100ms",
  },
  {
    id: "admin",
    to: "/dashboard/admin",
    icon: Shield,
    title: "Admin Management",
    description: "Manage user roles and permissions",
    gradient: "from-red-500 to-orange-600",
    delay: "150ms",
  },
  {
    id: "progress",
    to: "/dashboard/progress",
    icon: BookOpen,
    title: "Course Progress",
    description: "Track your learning journey and completion",
    gradient: "from-primary to-accent",
    delay: "200ms",
  },
  {
    id: "resources",
    to: "/dashboard/resources",
    icon: Library,
    title: "Resource Library",
    description: "Access templates, guides, and materials",
    gradient: "from-primary to-accent",
    delay: "300ms",
  },
  {
    id: "community",
    to: "/dashboard/community",
    icon: MessageSquare,
    title: "Community Hub",
    description: "Events, mentorship, polls, and activity feed",
    gradient: "from-primary to-accent",
    delay: "400ms",
  },
  {
    id: "skool",
    to: "https://www.skool.com/launch2revenue",
    icon: MessageSquare,
    title: "Skool Community",
    description: "Join our exclusive Skool community",
    gradient: "from-purple-500 to-indigo-600",
    delay: "450ms",
  },
  {
    id: "launch-calendar",
    to: "/dashboard/calendar",
    icon: Bell,
    title: "Launch Calendar",
    description: "Plan launch phases and set reminders",
    gradient: "from-purple-500 to-pink-600",
    delay: "550ms",
  },
  {
    id: "schedule",
    to: "/dashboard/schedule",
    icon: Calendar,
    title: "Schedule Control",
    description: "Plan your day and stay on track with your learning",
    gradient: "from-primary to-accent",
    delay: "600ms",
  },
  {
    id: "energy",
    to: "/dashboard/energy",
    icon: Battery,
    title: "Energy Tracker",
    description: "Monitor your energy and optimize productivity",
    gradient: "from-green-500 to-yellow-500",
    delay: "700ms",
  },
  {
    id: "goals",
    to: "/dashboard/goals",
    icon: Target,
    title: "Goal Planner",
    description: "Set, track, and achieve your goals effortlessly",
    gradient: "from-blue-500 to-purple-500",
    delay: "800ms",
  },
  {
    id: "moodboard",
    to: "/dashboard/moodboard",
    icon: Image,
    title: "Mood Board",
    description: "Collect inspiration and ideas to stay motivated",
    gradient: "from-pink-500 to-orange-500",
    delay: "900ms",
  },
  {
    id: "content-planner",
    to: "/dashboard/content-planner",
    icon: PenSquare,
    title: "Content Planner",
    description: "Plan and schedule your social media posts",
    gradient: "from-indigo-500 to-purple-600",
    delay: "950ms",
  },
  {
    id: "referrals",
    to: "/dashboard/referrals",
    icon: Gift,
    title: "Referrals & Earnings",
    description: "Track referrals and manage your affiliate earnings",
    gradient: "from-green-500 to-emerald-600",
    delay: "1000ms",
  },
  {
    id: "invoices",
    to: "/dashboard/invoices",
    icon: FileText,
    title: "Invoices & Billing",
    description: "View and download your invoices",
    gradient: "from-blue-500 to-cyan-600",
    delay: "1050ms",
  },
  {
    id: "support",
    to: "/dashboard/support",
    icon: HelpCircle,
    title: "Support Tickets",
    description: "Get help and track your support requests",
    gradient: "from-orange-500 to-red-600",
    delay: "1100ms",
  },
  {
    id: "sessions",
    to: "/dashboard/sessions",
    icon: Video,
    title: "Live Sessions",
    description: "Book and attend live coaching sessions",
    gradient: "from-purple-500 to-pink-600",
    delay: "1150ms",
  },
  {
    id: "affiliate-email",
    to: "/affiliate-email-automation",
    icon: Mail,
    title: "Affiliate Email Automation",
    description: "High-value email templates for affiliates (Premium)",
    gradient: "from-amber-500 to-orange-600",
    delay: "1200ms",
  },
  {
    id: "bookmarks",
    to: "/dashboard/bookmarks",
    icon: Bookmark,
    title: "Bookmarks",
    description: "Save and organize your favorite resources",
    gradient: "from-cyan-500 to-blue-600",
    delay: "1250ms",
  },
  {
    id: "notes",
    to: "/dashboard/notes",
    icon: FileText,
    title: "Course Notes",
    description: "Take notes on lessons and modules",
    gradient: "from-violet-500 to-purple-600",
    delay: "1300ms",
  },
  {
    id: "tasks",
    to: "/dashboard/tasks",
    icon: CheckSquare,
    title: "Task Manager",
    description: "Track your to-dos and stay organized",
    gradient: "from-emerald-500 to-green-600",
    delay: "1350ms",
  },
  {
    id: "focus",
    to: "/dashboard/focus",
    icon: Timer,
    title: "Focus Timer",
    description: "Pomodoro timer for deep work sessions",
    gradient: "from-red-500 to-pink-600",
    delay: "1400ms",
  },
  {
    id: "checkin",
    to: "/dashboard/checkin",
    icon: Heart,
    title: "Daily Check-in",
    description: "Track your mood and daily progress",
    gradient: "from-rose-500 to-pink-600",
    delay: "1450ms",
  },
  {
    id: "weekly-review",
    to: "/dashboard/weekly-review",
    icon: ClipboardCheck,
    title: "Weekly Review",
    description: "Reflect on your week and plan ahead",
    gradient: "from-indigo-500 to-blue-600",
    delay: "1500ms",
  },
  {
    id: "settings",
    to: "/dashboard/settings",
    icon: Settings,
    title: "Settings",
    description: "Customize your preferences and notifications",
    gradient: "from-gray-500 to-slate-600",
    delay: "1550ms",
  },
  {
    id: "avatar-generator",
    to: "/dashboard/avatar-generator",
    icon: Sparkles,
    title: "AI Avatar Generator",
    description: "Create stunning AI avatars from your photos",
    gradient: "from-purple-500 to-pink-600",
    delay: "1600ms",
  },
  {
    id: "partner",
    to: "/dashboard/partner",
    icon: Handshake,
    title: "Partner Dashboard",
    description: "Manage your brand partnerships and offers",
    gradient: "from-cyan-500 to-blue-600",
    delay: "1650ms",
  },
];

const SortableCard = ({ card, isEditMode, editTarget }: { card: DashboardCard; isEditMode: boolean; editTarget: 'sections' | 'cards' }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = card.icon;
  const isExternalLink = card.to.startsWith('http');

  const cardContent = (
    <>
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mb-4 shadow-lg shadow-primary/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors duration-300">
        {card.title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
    </>
  );

  const isEditingCards = isEditMode && editTarget === 'cards';

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {isEditingCards && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 z-10 cursor-grab active:cursor-grabbing p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
        >
          <GripVertical className="w-5 h-5 text-primary" />
        </div>
      )}
      {isExternalLink ? (
        <a
          href={card.to}
          target="_blank"
          rel="noopener noreferrer"
          className={`group relative block p-6 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm border border-border/50 rounded-xl hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 overflow-hidden ${
            isEditingCards ? "pointer-events-none" : ""
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative z-10">{cardContent}</div>
        </a>
      ) : (
        <Link
          to={card.to}
          className={`group relative block p-6 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm border border-border/50 rounded-xl hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 overflow-hidden ${
            isEditingCards ? "pointer-events-none" : ""
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative z-10">{cardContent}</div>
        </Link>
      )}
    </div>
  );
};

const SortableSection = ({ 
  section, 
  isEditMode,
  children 
}: { 
  section: LayoutSection; 
  isEditMode: boolean;
  children: React.ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditingThisLevel = isEditMode && (
    section.type === 'cards' ? false : true
  );

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {isEditingThisLevel && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-4 right-4 z-20 cursor-grab active:cursor-grabbing p-3 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors shadow-lg"
        >
          <GripVertical className="w-6 h-6 text-primary" />
        </div>
      )}
      <div>
        {children}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState<'sections' | 'cards'>('sections');
  const [isAdmin, setIsAdmin] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Scroll to top on mount - force immediate reset
  useEffect(() => {
    // Multiple approaches to ensure scroll works
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also try after a short delay in case content is still loading
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Mood Board State
  const [moodBoardItems, setMoodBoardItems] = useState<MoodBoardItem[]>(() => {
    const saved = localStorage.getItem("moodBoardItems");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [
          {
            id: 1,
            type: "note",
            content: "Focus on building strong revenue foundations",
            title: "Business Goal",
            color: "bg-blue-500/20",
          },
          {
            id: 2,
            type: "note",
            content: "Consistency beats intensity every time",
            title: "Daily Reminder",
            color: "bg-green-500/20",
          },
        ];
      }
    }
    return [
      {
        id: 1,
        type: "note",
        content: "Focus on building strong revenue foundations",
        title: "Business Goal",
        color: "bg-blue-500/20",
      },
      {
        id: 2,
        type: "note",
        content: "Consistency beats intensity every time",
        title: "Daily Reminder",
        color: "bg-green-500/20",
      },
    ];
  });

  const [newImageUrl, setNewImageUrl] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [selectedColor, setSelectedColor] = useState("bg-purple-500/20");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Background Customization State
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false);
  const [customBackgroundUrl, setCustomBackgroundUrl] = useState("");
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState<BackgroundOption>(() => {
    const saved = localStorage.getItem("dashboardBackground");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return presetBackgrounds[0];
      }
    }
    return presetBackgrounds[0];
  });

  const colors = [
    "bg-purple-500/20",
    "bg-blue-500/20",
    "bg-green-500/20",
    "bg-yellow-500/20",
    "bg-pink-500/20",
    "bg-orange-500/20",
  ];

  const [cards, setCards] = useState<DashboardCard[]>(() => {
    const savedOrder = localStorage.getItem("dashboardCardOrder");
    const defaultOrder = defaultCards.map((c) => c.id);
    if (savedOrder) {
      try {
        const orderIds: string[] = JSON.parse(savedOrder);
        // Keep existing order, but append any new default cards that weren't saved yet
        const ordered = orderIds
          .map((id) => defaultCards.find((c) => c.id === id))
          .filter(Boolean) as DashboardCard[];
        const missing = defaultCards.filter((c) => !orderIds.includes(c.id));
        const merged = [...ordered, ...missing];
        localStorage.setItem("dashboardCardOrder", JSON.stringify(merged.map((c) => c.id)));
        return merged;
      } catch {
        localStorage.setItem("dashboardCardOrder", JSON.stringify(defaultOrder));
        return defaultCards;
      }
    }
    // Initialize storage if it doesn't exist yet
    localStorage.setItem("dashboardCardOrder", JSON.stringify(defaultOrder));
    return defaultCards;
  });

  const defaultLayout: LayoutSection[] = [
    { id: 'cards', type: 'cards' },
    { id: 'tracker', type: 'tracker' },
    { id: 'ai-assistant', type: 'ai-assistant' },
    { id: 'moodboard', type: 'moodboard' },
  ];

  const [layoutSections, setLayoutSections] = useState<LayoutSection[]>(() => {
    const savedLayout = localStorage.getItem("dashboardLayout");
    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout);
        // Migrate old layouts: add ai-assistant section if it doesn't exist
        const hasAiAssistant = parsed.some((section: LayoutSection) => section.type === 'ai-assistant');
        if (!hasAiAssistant) {
          // Insert ai-assistant after tracker
          const trackerIndex = parsed.findIndex((section: LayoutSection) => section.type === 'tracker');
          if (trackerIndex !== -1) {
            parsed.splice(trackerIndex + 1, 0, { id: 'ai-assistant', type: 'ai-assistant' });
          } else {
            parsed.push({ id: 'ai-assistant', type: 'ai-assistant' });
          }
          // Save the migrated layout
          localStorage.setItem("dashboardLayout", JSON.stringify(parsed));
        }
        // Remove legacy 'skool' section if present
        const cleaned = parsed.filter((section: LayoutSection) => section.type !== 'skool');
        if (cleaned.length !== parsed.length) {
          localStorage.setItem("dashboardLayout", JSON.stringify(cleaned));
        }
        return cleaned;
      } catch {
        return defaultLayout;
      }
    }
    return defaultLayout;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLayoutSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem("dashboardLayout", JSON.stringify(newOrder));
        return newOrder;
      });
    }
  };

  const handleCardDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCards((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem("dashboardCardOrder", JSON.stringify(newOrder.map(c => c.id)));
        return newOrder;
      });
    }
  };

  const saveLayout = () => {
    setIsEditMode(false);
    toast.success("Dashboard layout saved!");
  };

  const resetLayout = () => {
    setLayoutSections(defaultLayout);
    localStorage.setItem("dashboardLayout", JSON.stringify(defaultLayout));
    // Also reset cards to include any newly added defaults (e.g., Launch Calendar)
    setCards(defaultCards);
    localStorage.setItem("dashboardCardOrder", JSON.stringify(defaultCards.map((c) => c.id)));
    toast.success("Layout reset to default");
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      const newItems = [
        ...moodBoardItems,
        {
          id: Date.now(),
          type: "image" as const,
          content: newImageUrl,
        },
      ];
      setMoodBoardItems(newItems);
      localStorage.setItem("moodBoardItems", JSON.stringify(newItems));
      setNewImageUrl("");
      setIsDialogOpen(false);
      toast.success("Image added to mood board!");
    }
  };

  const addNote = () => {
    if (newNoteContent.trim()) {
      const newItems = [
        ...moodBoardItems,
        {
          id: Date.now(),
          type: "note" as const,
          content: newNoteContent,
          title: newNoteTitle || "Note",
          color: selectedColor,
        },
      ];
      setMoodBoardItems(newItems);
      localStorage.setItem("moodBoardItems", JSON.stringify(newItems));
      setNewNoteTitle("");
      setNewNoteContent("");
      setIsDialogOpen(false);
      toast.success("Note added to mood board!");
    }
  };

  const deleteItem = (id: number) => {
    const newItems = moodBoardItems.filter((item) => item.id !== id);
    setMoodBoardItems(newItems);
    localStorage.setItem("moodBoardItems", JSON.stringify(newItems));
    toast.success("Item removed from mood board");
  };

  const applyBackground = (background: BackgroundOption) => {
    setSelectedBackground(background);
    localStorage.setItem("dashboardBackground", JSON.stringify(background));
    setBackgroundDialogOpen(false);
    toast.success("Background updated!");
  };

  const applyCustomBackground = () => {
    if (!customBackgroundUrl.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    const customBg: BackgroundOption = {
      id: 'custom',
      name: 'Custom',
      value: customBackgroundUrl,
      type: 'custom'
    };
    applyBackground(customBg);
    setCustomBackgroundUrl("");
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingBackground(true);

    try {
      // Convert to data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        const customBg: BackgroundOption = {
          id: 'custom',
          name: 'Custom Upload',
          value: dataUrl,
          type: 'custom'
        };
        applyBackground(customBg);
        setUploadingBackground(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingBackground(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading background:", error);
      toast.error("Failed to upload background image");
      setUploadingBackground(false);
    }
  };

  useEffect(() => {
    // Check session and load all data in parallel
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
        
        // Load profile and role data in parallel for speed
        const [profileResult, roleResult] = await Promise.all([
          supabase
            .from("profiles")
            .select("username")
            .eq("id", session.user.id)
            .maybeSingle(),
          supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id)
            .eq("role", "admin")
            .maybeSingle()
        ]);
        
        // Set username
        if (profileResult.data?.username) {
          setUserName(profileResult.data.username);
        } else {
          const emailUsername = session.user.email?.split("@")[0] || "";
          const metaName = session.user.user_metadata?.full_name || "";
          setUserName(metaName || emailUsername);
        }
        
        // Set admin status
        const adminStatus = !!roleResult.data;
        setIsAdmin(adminStatus);
        
        // Fetch notifications if admin
        if (adminStatus) {
          fetchNotifications(session.user.id);
        }
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setLoading(false);
        navigate("/auth");
        return;
      }

      // Only synchronous state updates here per best practices
      setUser(session.user);
      setLoading(false);

      // Defer Supabase queries to avoid deadlocks
      setTimeout(async () => {
        const [profileResult, roleResult] = await Promise.all([
          supabase
            .from("profiles")
            .select("username")
            .eq("id", session.user.id)
            .maybeSingle(),
          supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id)
            .eq("role", "admin")
            .maybeSingle()
        ]);

        if (profileResult.data?.username) {
          setUserName(profileResult.data.username);
        } else {
          const emailUsername = session.user.email?.split("@")[0] || "";
          const metaName = session.user.user_metadata?.full_name || "";
          setUserName(metaName || emailUsername);
        }

        const adminStatus = !!roleResult.data;
        setIsAdmin(adminStatus);

        if (adminStatus) {
          fetchNotifications(session.user.id);
        }
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchNotifications = async (userId: string) => {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching notifications:", error);
      return;
    }

    setNotifications(data || []);
    setUnreadCount(data?.filter(n => !n.read).length || 0);
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", notificationId);

    if (error) {
      console.error("Error marking notification as read:", error);
      return;
    }

    fetchNotifications(user.id);
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("read", false);

    if (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark all as read");
      return;
    }

    fetchNotifications(user.id);
    toast.success("All notifications marked as read");
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
      toast.success("Signed out successfully");
    } catch (error) {
      console.log("Logout global failed, clearing local session:", error);
      await supabase.auth.signOut({ scope: 'local' });
      toast.success("Signed out successfully");
    } finally {
      // Always navigate away, even if API call fails
      navigate("/");
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Good morning";
    } else if (hour >= 12 && hour < 17) {
      return "Good afternoon";
    } else if (hour >= 17 && hour < 22) {
      return "Good evening";
    } else {
      return "Good night";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getBackgroundStyle = () => {
    if (selectedBackground.type === 'custom') {
      return {
        backgroundImage: `url(${selectedBackground.value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      };
    }
    if (selectedBackground.value === 'mesh-gradient') {
      return {
        background: 'var(--gradient-mesh)',
        backgroundColor: 'hsl(var(--background))'
      };
    }
    return {};
  };

  return (
    <div 
      className={`min-h-screen relative overflow-hidden ${selectedBackground.value !== 'mesh-gradient' && selectedBackground.type !== 'custom' ? selectedBackground.value : ''}`}
      style={getBackgroundStyle()}
    >
      {/* Bold Animated Background Effects for mesh gradient */}
      {selectedBackground.value === 'mesh-gradient' && (
        <>
          <div className="absolute inset-0 -z-10 opacity-30">
            <div className="absolute top-0 -left-20 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 -right-20 w-96 h-96 bg-accent/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          </div>
        </>
      )}
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col gap-2">
            {/* Top row - Logo and primary actions */}
            <div className="flex justify-between items-center">
              <Link to="/" className="text-xl md:text-2xl font-bold text-primary">
                Launch To Revenue
              </Link>
              <div className="flex items-center gap-1.5 md:gap-2">
                {isAdmin && (
                  <>
                    {/* Financial Alert Badge - Prominent on mobile */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/admin/pricing")}
                      className="bg-green-500/10 border-green-500/30 hover:bg-green-500/20"
                    >
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </Button>

                    {/* Announcements Control */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/admin/announcements")}
                      className="bg-primary/10 border-primary/30 hover:bg-primary/20"
                      title="Manage Announcements"
                    >
                      <Megaphone className="w-4 h-4" />
                    </Button>
                    
                    {/* Notifications Bell */}
                    <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="relative">
                          <Bell className="w-4 h-4" />
                          {unreadCount > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
                            >
                              {unreadCount}
                            </Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96 p-0" align="end">
                        <div className="flex items-center justify-between p-4 border-b">
                          <h3 className="font-semibold">Notifications</h3>
                          {unreadCount > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={markAllAsRead}
                              className="text-xs"
                            >
                              Mark all read
                            </Button>
                          )}
                        </div>
                        <ScrollArea className="h-[400px]">
                          {notifications.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                              No notifications yet
                            </div>
                          ) : (
                            <div className="divide-y">
                              {notifications.map((notification) => (
                                <div
                                  key={notification.id}
                                  className={`p-4 hover:bg-muted/50 transition-colors ${
                                    !notification.read ? "bg-primary/5" : ""
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-semibold text-sm">
                                          {notification.title}
                                        </h4>
                                        {!notification.read && (
                                          <Badge variant="secondary" className="text-xs">
                                            New
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground mb-2">
                                        {notification.message}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(notification.created_at).toLocaleDateString()} at{" "}
                                        {new Date(notification.created_at).toLocaleTimeString()}
                                      </p>
                                    </div>
                                    {!notification.read && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => markAsRead(notification.id)}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                  {notification.type === "consultation_request" && notification.related_id && (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="w-full mt-2"
                                      onClick={() => {
                                        setNotificationsOpen(false);
                                        navigate(`/consultation-request/${notification.related_id}`);
                                      }}
                                    >
                                      View Request
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </PopoverContent>
                    </Popover>
                  </>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBackgroundDialogOpen(true)}
                  className="hidden md:flex"
                >
                  <Palette className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Background</span>
                </Button>
              </div>
            </div>
            
            {/* Second row - Admin actions (stacked on mobile) */}
            {isAdmin && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/dashboard/admin")}
                  className="text-xs"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  <span>Admin</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin/referrals")}
                  className="text-xs"
                >
                  <Gift className="w-3 h-3 mr-1" />
                  <span>Referrals</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/discount-codes")}
                  className="text-xs"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  <span>Codes</span>
                </Button>
                <Button
                  variant={isEditMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => (isEditMode ? saveLayout() : setIsEditMode(true))}
                  className="text-xs ml-auto"
                >
                  {isEditMode ? (
                    <>
                      <Save className="w-3 h-3 mr-1" />
                      <span>Save</span>
                    </>
                  ) : (
                    <>
                      <GripVertical className="w-3 h-3 mr-1" />
                      <span>Edit</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-xs"
                >
                  <LogOut className="w-3 h-3 mr-1" />
                  <span>Sign Out</span>
                </Button>
              </div>
            )}
            
            {/* Non-admin sign out button */}
            {!isAdmin && (
              <div className="flex justify-between items-center">
                <Button
                  variant={isEditMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => (isEditMode ? saveLayout() : setIsEditMode(true))}
                  className="text-xs"
                >
                  {isEditMode ? (
                    <>
                      <Save className="w-3 h-3 mr-1" />
                      <span>Save Layout</span>
                    </>
                  ) : (
                    <>
                      <GripVertical className="w-3 h-3 mr-1" />
                      <span>Edit Layout</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-xs"
                >
                  <LogOut className="w-3 h-3 mr-1" />
                  <span>Sign Out</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-7xl mx-auto space-y-10">
          {/* System Announcements */}
          <AnnouncementBanner />
          
          <div className="text-center space-y-4 animate-fade-in">
            <div className="inline-block">
              <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-gradient">
                {getGreeting()}, {userName || "there"}! 👋
              </h1>
            </div>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Track your progress and access all course materials
            </p>
            {isEditMode && (
              <div className="flex flex-col items-center gap-3 pt-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <p className="text-sm text-primary font-medium">
                    Edit Mode Active
                  </p>
                </div>
                <Tabs value={editTarget} onValueChange={(v) => setEditTarget(v as 'sections' | 'cards')} className="w-auto">
                  <TabsList className="bg-background/50 backdrop-blur-sm">
                    <TabsTrigger value="sections">Reorder Sections</TabsTrigger>
                    <TabsTrigger value="cards">Reorder Cards</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}
          </div>

          {/* Draggable Layout Sections */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext items={layoutSections.map(s => s.id)}>
              <div className="space-y-12">
                {layoutSections.map((section) => (
                  <SortableSection key={section.id} section={section} isEditMode={isEditMode}>
                  {section.type === 'cards' && (
                      isEditMode ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleCardDragEnd}
                        >
                          <SortableContext items={cards.map(c => c.id)} strategy={rectSortingStrategy}>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                              {cards.map((card) => {
                                // Hide admin card for non-admin users
                                if (card.id === 'admin' && !isAdmin) return null;
                                return <SortableCard key={card.id} card={card} isEditMode={isEditMode} editTarget={editTarget} />;
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                          {cards.map((card) => {
                            // Hide admin card for non-admin users
                            if (card.id === 'admin' && !isAdmin) return null;
                            return <SortableCard key={card.id} card={card} isEditMode={isEditMode} editTarget={editTarget} />;
                          })}
                        </div>
                      )
                    )}
                    
                    {section.type === 'tracker' && (
                      <LaunchTracker />
                    )}
                    
                    {section.type === 'ai-assistant' && (
                      <section className="py-8 px-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl">
                        <div className="max-w-4xl mx-auto">
                          <div className="mb-6 text-center">
                            <h2 className="text-3xl font-bold mb-2">AI Launch Assistant</h2>
                            <p className="text-muted-foreground">
                              Get personalized guidance for your launch journey
                            </p>
                          </div>
                          <AIAssistant mode="inline" />
                        </div>
                      </section>
                    )}
                    
                    
                    {section.type === 'moodboard' && (
                      <section className="py-16 px-4 bg-gradient-to-br from-pink-500/5 to-orange-500/10 rounded-3xl">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-12">
                <div>
                  <h2 className="text-4xl font-bold mb-4">Mood Board</h2>
                  <p className="text-xl text-muted-foreground">
                    Collect inspiration, ideas, and reminders to keep you motivated
                  </p>
                </div>

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="lg" className="gap-2">
                      <Plus className="w-5 h-5" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>Add to Mood Board</DialogTitle>
                      <DialogDescription>
                        Add an image or note to your mood board for inspiration
                      </DialogDescription>
                    </DialogHeader>

                    <Tabs defaultValue="note" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="note">
                          <Type className="w-4 h-4 mr-2" />
                          Note
                        </TabsTrigger>
                        <TabsTrigger value="image">
                          <Image className="w-4 h-4 mr-2" />
                          Image
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="note" className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Title (Optional)</label>
                          <Input
                            placeholder="Enter note title..."
                            value={newNoteTitle}
                            onChange={(e) => setNewNoteTitle(e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">Content</label>
                          <Textarea
                            placeholder="Enter your note content..."
                            value={newNoteContent}
                            onChange={(e) => setNewNoteContent(e.target.value)}
                            rows={4}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">Color</label>
                          <div className="flex gap-2">
                            {colors.map((color) => (
                              <button
                                key={color}
                                onClick={() => setSelectedColor(color)}
                                className={`w-10 h-10 rounded-lg ${color} border-2 ${
                                  selectedColor === color
                                    ? "border-primary scale-110"
                                    : "border-transparent"
                                } transition-all hover:scale-105`}
                              />
                            ))}
                          </div>
                        </div>

                        <DialogFooter>
                          <Button onClick={addNote}>Add Note</Button>
                        </DialogFooter>
                      </TabsContent>

                      <TabsContent value="image" className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Image URL</label>
                          <Input
                            placeholder="Enter image URL..."
                            value={newImageUrl}
                            onChange={(e) => setNewImageUrl(e.target.value)}
                          />
                        </div>

                        {newImageUrl && (
                          <div className="rounded-lg overflow-hidden border">
                            <img
                              src={newImageUrl}
                              alt="Preview"
                              className="w-full h-48 object-cover"
                              onError={() => toast.error("Invalid image URL")}
                            />
                          </div>
                        )}

                        <DialogFooter>
                          <Button onClick={addImage}>Add Image</Button>
                        </DialogFooter>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Mood Board Grid */}
              {moodBoardItems.length > 0 ? (
                <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
                  {moodBoardItems.map((item) => (
                    <Card
                      key={item.id}
                      className="break-inside-avoid group relative hover:shadow-xl transition-all duration-300 overflow-hidden"
                    >
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      {item.type === "image" ? (
                        <img
                          src={item.content}
                          alt="Mood board item"
                          className="w-full h-auto object-cover"
                        />
                      ) : (
                        <CardContent className={`p-6 ${item.color}`}>
                          {item.title && (
                            <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {item.content}
                          </p>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="inline-flex p-6 rounded-full bg-primary/10 mb-6">
                    <Image className="w-12 h-12 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Your Mood Board is Empty</h3>
                  <p className="text-muted-foreground mb-6">
                    Start adding images and notes to create your inspiration board
                  </p>
                  <Button onClick={() => setIsDialogOpen(true)} size="lg">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Your First Item
                  </Button>
                </div>
              )}
            </div>
                      </section>
                    )}
                  </SortableSection>
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Quick Links Section */}
          <section className="py-8 px-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl">
            <div className="max-w-6xl mx-auto">
              <div className="mb-6 text-center">
                <h2 className="text-3xl font-bold mb-2">Quick Links</h2>
                <p className="text-muted-foreground">
                  Access community features and connections
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link to="/dashboard/messages">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                          <Mail className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Direct Messages</h3>
                          <p className="text-sm text-muted-foreground">Chat with members</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/dashboard/discussions">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <MessageSquare className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Discussions</h3>
                          <p className="text-sm text-muted-foreground">Join conversations</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/community/calendar">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Community Calendar</h3>
                          <p className="text-sm text-muted-foreground">Upcoming events</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>
          </section>

          {/* Admin Tools Section */}
          {isAdmin && (
            <section className="py-8 px-4 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-3xl border-2 border-red-500/20">
              <div className="max-w-6xl mx-auto">
                <div className="mb-6 text-center">
                  <h2 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
                    <Shield className="w-8 h-8 text-red-500" />
                    Admin Tools
                  </h2>
                  <p className="text-muted-foreground">
                    Manage platform features and view analytics
                  </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Link to="/admin/mentorship">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-red-500/20">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Mentorship</h3>
                            <p className="text-sm text-muted-foreground">Manage matches</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/admin/reports">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-red-500/20">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Monthly Reports</h3>
                            <p className="text-sm text-muted-foreground">View analytics</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/admin/call-requests">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-red-500/20">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                            <Phone className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Call Requests</h3>
                            <p className="text-sm text-muted-foreground">Manage consultations</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/dashboard/analytics">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-red-500/20">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Analytics</h3>
                            <p className="text-sm text-muted-foreground">Growth metrics</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Background Customization Dialog */}
      <Dialog open={backgroundDialogOpen} onOpenChange={setBackgroundDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Customize Dashboard Background</DialogTitle>
            <DialogDescription>
              Choose from preset backgrounds or add your own custom image
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="presets" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="presets">Presets</TabsTrigger>
              <TabsTrigger value="custom">Custom Image</TabsTrigger>
            </TabsList>

            <TabsContent value="presets" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {presetBackgrounds.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => applyBackground(bg)}
                    className={`relative h-24 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedBackground.id === bg.id
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-full h-full ${bg.value}`} />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-white font-medium">{bg.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Upload from Device</label>
                  <div className="flex flex-col gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploadingBackground}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload an image from your phone or computer (max 5MB)
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or use URL</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Image URL</label>
                  <Input
                    placeholder="https://example.com/image.jpg"
                    value={customBackgroundUrl}
                    onChange={(e) => setCustomBackgroundUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter a URL to an image you'd like to use as your background
                  </p>
                </div>

                {customBackgroundUrl && (
                  <div className="rounded-lg overflow-hidden border h-48">
                    <img
                      src={customBackgroundUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={() => toast.error("Invalid image URL")}
                    />
                  </div>
                )}

                {customBackgroundUrl && (
                  <DialogFooter>
                    <Button onClick={applyCustomBackground}>Apply Custom Background</Button>
                  </DialogFooter>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
